// Top-level Client Portal Sites page (Session AFH): the customer's sites as a flat row-list, the
// same treatment the Overview dashboard uses (shared PortalSiteList). Each row links to the site
// detail page. NO pricing/contract value, engineer names or internal notes. A lapsed session (401)
// sends the customer back to sign in.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalOverview } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { PortalSiteList } from "./PortalSiteList";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

export function PortalSitesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PortalOverview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    api.portal
      .overview()
      .then((d) => {
        if (live) setData(d);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!data) return <FullScreenLoader />;

  return (
    <PortalShell wide userName={data.customerName} onSignOut={logout}>
      <header className="mb-6">
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">Your sites</h1>
        <p className="mt-1 text-ele-body">Tap a site to see its lifts, compliance and history.</p>
      </header>
      <PortalSiteList sites={data.sites} />
    </PortalShell>
  );
}
